import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ProjectProvider } from '@/context/ProjectContext';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import BackToTop from '@/components/BackToTop';

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

import LandingPage from '@/pages/LandingPage';
import MarketingFlyer from '@/pages/MarketingFlyer';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DelayActionPage from '@/pages/DelayActionPage';
import AdminDashboard from '@/pages/AdminDashboard';
import ContractorDashboard from '@/pages/ContractorDashboard';
import ProjectDetail from '@/pages/ProjectDetail';
import ProjectSetup from '@/pages/ProjectSetup';
import ProjectSummaryDashboard from '@/pages/ProjectSummaryDashboard';
import ClientDashboard from '@/pages/ClientDashboard';
import TasksPage from '@/pages/TasksPage';
import EventDetail from '@/pages/EventDetail';
import NoticeDetail from '@/pages/NoticeDetail';
import ClaimDetail from '@/pages/ClaimDetail';
import SummarySchedule from '@/pages/SummarySchedule';
import ReportsPage from '@/pages/ReportsPage';
import RFIDetail from '@/pages/RFIDetail';
import SICreate from '@/pages/SICreate';
import SIDetail from '@/pages/SIDetail';
import PublicSIComment from '@/pages/PublicSIComment';
import PublicSIClarification from '@/pages/PublicSIClarification';
import SendToTaskResponse from '@/pages/SendToTaskResponse';
import DrawingDetail from '@/pages/DrawingDetail';
import CompanySettings from '@/pages/CompanySettings';
import BillingDashboard from '@/pages/BillingDashboard';
import CPMScheduler from '@/pages/CPMScheduler';
import CRMDashboard from '@/pages/CRMDashboard';
import DocumentEngine from '@/pages/DocumentEngine';
import MeetingMinutes from '@/pages/MeetingMinutes';
import MeetingMinutesDetail from '@/pages/MeetingMinutesDetail';
import ActionPacks from '@/pages/ActionPacks';
import ShopDrawingsPage from '@/pages/ShopDrawingsPage';
import ShopDrawingDetail from '@/pages/ShopDrawingDetail';
import SubcontractorPortal from '@/pages/SubcontractorPortal';
import CloseoutPortalPage from '@/pages/CloseoutPortalPage';
import ConsultantPortal from '@/pages/ConsultantPortal';
import ProjectReports from '@/pages/ProjectReports';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import AdminUserManagement from '@/pages/AdminUserManagement';
import HelpPortal from '@/pages/HelpPortal';
import KnowledgeBase from '@/pages/KnowledgeBase';
import AdminFeedbackDashboard from '@/pages/AdminFeedbackDashboard';
import VariationOrders from '@/pages/VariationOrders';
import VODetail from '@/pages/VODetail';
import AwaitingItems from '@/pages/AwaitingItems';
import ProcurementSchedulePage from '@/pages/ProcurementSchedulePage';
import IRSSchedulePage from '@/pages/IRSSchedulePage';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import POPIACompliance from '@/pages/POPIACompliance';
import ProjectMap from '@/pages/ProjectMap';
import ExecutiveDashboard from '@/pages/ExecutiveDashboard';
import CloseoutEngine from '@/pages/CloseoutEngine';
import DisputeEngine from '@/pages/DisputeEngine';
import CorrespondenceDashboard from '@/pages/CorrespondenceDashboard';
import PlantEquipmentPage from '@/pages/PlantEquipmentPage';
import SiteDiaryPage from '@/pages/SiteDiaryPage';
import DelayDetectionPage from '@/pages/DelayDetectionPage';
import PeriodicReports from '@/pages/PeriodicReports';
import EmailTemplatePreview from '@/pages/EmailTemplatePreview';

import UserSettingsPage from '@/pages/UserSettingsPage';
import { WalkthroughProvider, WalkthroughOverlay } from '@/components/walkthrough';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-mono">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/marketing-flyer" element={<MarketingFlyer />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/delay-action" element={<DelayActionPage />} />
      {/* Public routes - no auth required */}
      <Route path="/public/si-comment/:tokenId" element={<PublicSIComment />} />
      <Route path="/public/si-clarification/:tokenId" element={<PublicSIClarification />} />
      <Route path="/respond/:token" element={<SendToTaskResponse />} />
      <Route path="/subcontractor-portal/:token" element={<SubcontractorPortal />} />
      <Route path="/closeout-portal/:token" element={<CloseoutPortalPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      {/* Consultant Portal - requires login but accessible to consultant roles */}
      <Route
        path="/consultant-portal"
        element={
          <ProtectedRoute>
            <ConsultantPortal />
          </ProtectedRoute>
        }
      />
      {/* Landing page for non-logged-in users, dashboard for logged-in users */}
      <Route
        path="/"
        element={
          user ? (
            user.role === 'admin' ? <AdminDashboard /> : <ContractorDashboard />
          ) : (
            <LandingPage />
          )
        }
      />
      {/* Explicit dashboard route for logged-in users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user?.role === 'admin' ? <AdminDashboard /> : <ContractorDashboard />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/awaiting"
        element={
          <ProtectedRoute>
            <AwaitingItems />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPortal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            <KnowledgeBase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/diary"
        element={
          <ProtectedRoute>
            <SiteDiaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/periodic-reports"
        element={
          <ProtectedRoute>
            <PeriodicReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/plant"
        element={
          <ProtectedRoute>
            <PlantEquipmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/delay-detection"
        element={
          <ProtectedRoute>
            <DelayDetectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/setup"
        element={
          <ProtectedRoute>
            <ProjectSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/closeout"
        element={
          <ProtectedRoute>
            <CloseoutEngine />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/disputes"
        element={
          <ProtectedRoute>
            <DisputeEngine />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/dashboard"
        element={
          <ProtectedRoute>
            <ProjectSummaryDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <ProtectedRoute>
            <EventDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notices/:noticeId"
        element={
          <ProtectedRoute>
            <NoticeDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/:claimId"
        element={
          <ProtectedRoute>
            <ClaimDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/summary"
        element={
          <ProtectedRoute>
            <SummarySchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/rfi/:rfiId"
        element={
          <ProtectedRoute>
            <RFIDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/drawings/:drawingId"
        element={
          <ProtectedRoute>
            <DrawingDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/si/create"
        element={
          <ProtectedRoute>
            <SICreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/si/:siId"
        element={
          <ProtectedRoute>
            <SIDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-dashboard"
        element={
          <ProtectedRoute>
            <ExecutiveDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-map"
        element={
          <ProtectedRoute>
            <ProjectMap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/correspondence"
        element={
          <ProtectedRoute>
            <CorrespondenceDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/reports"
        element={
          <ProtectedRoute>
            <ProjectReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/meeting-minutes"
        element={
          <ProtectedRoute>
            <MeetingMinutes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meeting-minutes/:minutesId"
        element={
          <ProtectedRoute>
            <MeetingMinutesDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/action-packs"
        element={
          <ProtectedRoute>
            <ActionPacks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/shop-drawings"
        element={
          <ProtectedRoute>
            <ShopDrawingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop-drawings/:packageId"
        element={
          <ProtectedRoute>
            <ShopDrawingDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-settings"
        element={
          <ProtectedRoute>
            <CompanySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <UserSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute adminOnly>
            <BillingDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <ProtectedRoute adminOnly>
            <CRMDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute adminOnly>
            <DocumentEngine />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute adminOnly>
            <AdminFeedbackDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-templates"
        element={
          <ProtectedRoute adminOnly>
            <EmailTemplatePreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/variation-orders"
        element={
          <ProtectedRoute>
            <VariationOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/vos"
        element={
          <ProtectedRoute>
            <VariationOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/variation-orders/:voId"
        element={
          <ProtectedRoute>
            <VODetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/vos/:voId"
        element={
          <ProtectedRoute>
            <VODetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/cpm"
        element={
          <ProtectedRoute>
            <CPMScheduler />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/procurement"
        element={
          <ProtectedRoute>
            <ProcurementSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/irs"
        element={
          <ProtectedRoute>
            <IRSSchedulePage />
          </ProtectedRoute>
        }
      />
      {/* Legal Pages - Public Routes */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/popia" element={<POPIACompliance />} />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <AuthProvider>
          <ProjectProvider>
            <WalkthroughProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AppRoutes />
                <BackToTop />
                <WalkthroughOverlay />
                <Toaster />
              </BrowserRouter>
            </WalkthroughProvider>
          </ProjectProvider>
        </AuthProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;